// src/utils/normalizeError.js
//
// Turns any thrown value — an axios/HTTP error, a native Error, a string, or an
// arbitrary object — into one flat, displayable shape consumed by the
// notification UI. Keeping this in one place means the toast, the ErrorBoundary,
// and the global handlers all show errors consistently.

function safeStringify(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

function isAxiosError(error) {
    return !!(error && typeof error === 'object' && (error.isAxiosError || error.response || error.config));
}

// A short, human title derived from the HTTP status.
function titleForStatus(status) {
    if (!status) return 'Network error';
    if (status >= 500) return `Server error (${status})`;
    if (status === 404) return `Not found (${status})`;
    if (status === 403) return `Access denied (${status})`;
    if (status === 401) return `Session expired (${status})`;
    if (status === 400) return `Invalid request (${status})`;
    if (status === 429) return `Too many requests (${status})`;
    return `Request failed (${status})`;
}

/**
 * @param {*} error  Anything that was thrown / rejected.
 * @param {{title?:string, detail?:*, source?:string}} [opts]
 * @returns {object} Normalized notification fields.
 */
export function normalizeError(error, opts = {}) {
    const base = {
        title: opts.title || 'Something went wrong',
        message: '',
        detail: opts.detail,
        source: opts.source || 'app',
        status: undefined,
        statusText: undefined,
        code: undefined,
        requestId: undefined,
        method: undefined,
        url: undefined,
        stack: undefined,
        responseBody: undefined,
        name: undefined,
    };

    // ── Axios / HTTP error ───────────────────────────────────────────────────
    if (isAxiosError(error)) {
        const res = error.response;
        const cfg = error.config || {};
        const data = res?.data;
        const headers = res?.headers || {};

        base.status = res?.status;
        base.statusText = res?.statusText;
        base.method = (cfg.method || '').toUpperCase() || undefined;
        base.url = cfg.url || undefined;
        base.name = error.name;
        base.stack = error.stack;
        // Backend code first (e.g. NOT_FOUND), else axios code (e.g. ERR_NETWORK).
        base.code = (data && data.code) || error.code;
        base.requestId =
            (data && data.requestId) || headers['x-request-id'] || headers['X-Request-Id'];

        // Prefer the backend's safe clientMessage over axios's generic string.
        const backendMsg = typeof data === 'string' ? data : data?.message;
        base.message = backendMsg || error.message || 'Request failed';

        // Keep the structured response body for the expandable detail view.
        if (data !== undefined && typeof data !== 'string') {
            base.responseBody = data;
        }

        if (!opts.title) {
            base.title =
                error.code === 'ERR_NETWORK'
                    ? 'Network error — could not reach the server'
                    : titleForStatus(base.status);
        }
        return base;
    }

    // ── Native Error ─────────────────────────────────────────────────────────
    if (error instanceof Error) {
        base.name = error.name;
        base.message = error.message || String(error);
        base.stack = error.stack;
        if (!opts.title) {
            base.title = error.name && error.name !== 'Error' ? error.name : 'Unexpected error';
        }
        return base;
    }

    // ── Plain string ─────────────────────────────────────────────────────────
    if (typeof error === 'string') {
        base.message = error;
        return base;
    }

    // ── Anything else ────────────────────────────────────────────────────────
    base.message = safeStringify(error) || 'Unknown error';
    return base;
}
